import { applyParamPatch } from "./url-params";

/**
 * The create/update drawer the collection URL is currently asking for.
 * `open` wins over `new` when both are present. `defaults` stays on the
 * URL for a related-record "add" link; it is not part of the target identity.
 */
export type DrawerTarget = { kind: "edit"; id: string } | { kind: "create" } | null;

export function drawerTargetFromParams(
  params: URLSearchParams
): Exclude<DrawerTarget, null> | null {
  const open = params.get("open");
  if (open) return { kind: "edit", id: open };
  if (params.get("new") === "1") return { kind: "create" };
  return null;
}

/** Collection is part of the key so the same id on another table still opens. */
export function drawerTargetKey(
  collection: string,
  target: DrawerTarget
): string | null {
  if (!target) return null;
  if (target.kind === "edit") return `${collection}\0edit\0${target.id}`;
  return `${collection}\0create`;
}

/**
 * Write or clear the drawer keys. Phone search (`q`, `page`, `limit`),
 * `sort`, and `filterGroups` are left alone.
 * Closing clears `defaults` too, so a shared edit link does not keep a
 * create prefill from an earlier related-record hop.
 */
export function withDrawerTarget(
  prev: URLSearchParams,
  target: DrawerTarget
): URLSearchParams {
  if (!target) {
    return applyParamPatch(prev, {
      open: undefined,
      new: undefined,
      defaults: undefined,
    });
  }
  if (target.kind === "edit") {
    return applyParamPatch(prev, {
      open: target.id,
      new: undefined,
      defaults: undefined,
    });
  }
  return applyParamPatch(prev, {
    open: undefined,
    new: "1",
    defaults: undefined,
  });
}
